import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import useAuth from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";

const positions = ["GS", "GA", "WA", "C", "WD", "GD", "GK"];

function SetupMatch() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [match, setMatch] = useState(null);
  const [team, setTeam] = useState(null);
  const [form, setForm] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      setLoading(true);
      const matchRef = doc(db, "games", `demoGame_${user.uid}`);
      const teamRef = doc(db, "teams", `demoTeam_${user.uid}`);
      const matchSnap = await getDoc(matchRef);
      const teamSnap = await getDoc(teamRef);
      if (matchSnap.exists() && teamSnap.exists()) {
        setMatch(matchSnap.data());
        setTeam(teamSnap.data());
        setForm({
          opponent: matchSnap.data().opponent,
          date: matchSnap.data().date,
          time: matchSnap.data().time,
          matchType: matchSnap.data().matchType,
          quarterLength: matchSnap.data().quarterLength,
          startingLineup: [...matchSnap.data().startingLineup],
        });
      }
      setLoading(false);
    };
    fetchData();
  }, [user]);

  // Handler to update form values
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Assign a player to a position in startingLineup
  const handleLineupChange = (pos, playerId) => {
    const player = team.squad.find((p) => p.playerId === playerId);
    setForm((prev) => {
      // Remove any player already in startingLineup
      let newLineup = prev.startingLineup.filter((p) => p.position !== pos && p.playerId !== playerId);
      // Add the player to the new position
      newLineup.push({ playerId, name: player.name, position: pos });
      // Always keep exactly one player per position
      return { ...prev, startingLineup: newLineup };
    });
  };

  // Build the startingLineup for display
  const getPlayerForPosition = (pos) => {
    return form.startingLineup.find((p) => p.position === pos)?.playerId || "";
  };

  // Get the rest of the squad as subs (not in startingLineup)
  const getSubs = () => {
    const startingIds = form.startingLineup.map((p) => p.playerId);
    return team.squad.filter((p) => !startingIds.includes(p.playerId));
  };

  const handleSave = async () => {
    const matchRef = doc(db, "games", `demoGame_${user.uid}`);
    await updateDoc(matchRef, {
      opponent: form.opponent,
      date: form.date,
      time: form.time,
      matchType: form.matchType,
      quarterLength: Number(form.quarterLength),
      startingLineup: form.startingLineup,
      subs: getSubs().map((p) => ({ playerId: p.playerId, name: p.name })),
      status: "not started"
    });
    alert("Match updated!");
    navigate("/dashboard");
  };

  const handleStart = async () => {
    const matchRef = doc(db, "games", `demoGame_${user.uid}`);
    await updateDoc(matchRef, {
      opponent: form.opponent,
      date: form.date,
      time: form.time,
      matchType: form.matchType,
      quarterLength: Number(form.quarterLength),
      startingLineup: form.startingLineup,
      subs: getSubs().map((p) => ({ playerId: p.playerId, name: p.name })),
      status: "live"
    });
    navigate("/match");
  };

  if (loading || !form || !team) return <div>Loading match setup...</div>;

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", padding: 24 }}>
      <h2>Setup Match</h2>
      <div>
        <label>
          <b>Opponent:</b>
          <input value={form.opponent} onChange={e => handleChange("opponent", e.target.value)} style={{ marginLeft: 8, marginBottom: 8 }} />
        </label>
        <br />
        <label>
          <b>Date:</b>
          <input type="date" value={form.date} onChange={e => handleChange("date", e.target.value)} style={{ marginLeft: 8, marginBottom: 8 }} />
        </label>
        <br />
        <label>
          <b>Time:</b>
          <input type="time" value={form.time} onChange={e => handleChange("time", e.target.value)} style={{ marginLeft: 8, marginBottom: 8 }} />
        </label>
        <br />
        <label>
          <b>Match Type:</b>
          <select value={form.matchType} onChange={e => handleChange("matchType", e.target.value)} style={{ marginLeft: 8, marginBottom: 8 }}>
            <option>League</option>
            <option>Friendly</option>
            <option>Competition</option>
            <option>County</option>
            <option>Regionals</option>
            <option>Nationals</option>
          </select>
        </label>
        <br />
        <label>
          <b>Quarter Length (minutes):</b>
          <select value={form.quarterLength} onChange={e => handleChange("quarterLength", e.target.value)} style={{ marginLeft: 8, marginBottom: 8 }}>
            <option value={9}>9</option>
            <option value={10}>10</option>
            <option value={12}>12</option>
            <option value={15}>15</option>
          </select>
        </label>
      </div>
      <hr style={{ margin: "16px 0" }} />
      <h3>Assign Starting 7</h3>
      <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 8 }}>
        {positions.map((pos) => (
          <React.Fragment key={pos}>
            <div><b>{pos}</b></div>
            <select
              value={getPlayerForPosition(pos)}
              onChange={e => handleLineupChange(pos, e.target.value)}
            >
              <option value="">-- Select Player --</option>
              {team.squad.map((p) => (
                // Only allow a player to be picked for one position at a time
                <option
                  key={p.playerId}
                  value={p.playerId}
                  disabled={form.startingLineup.some(lp => lp.playerId === p.playerId && lp.position !== pos)}
                >
                  {p.name}
                </option>
              ))}
            </select>
          </React.Fragment>
        ))}
      </div>
      <div style={{ margin: "16px 0" }}>
        <b>Subs:</b> {getSubs().map((p) => p.name).join(", ") || "None"}
      </div>
      <div>
        <button onClick={handleSave} style={{ marginRight: 8 }}>Save</button>
        <button onClick={handleStart} style={{ background: "#22c55e", color: "#fff" }}>Start Match</button>
      </div>
    </div>
  );
}

export default SetupMatch;
